import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { 
  MdDashboard, 
  MdInventory, 
  MdCategory, 
  MdLogout, 
  MdMenu, 
  MdClose,
  MdPerson,
  MdShoppingCart,
  MdSettings,
  MdVideoLibrary,
  MdImage,
  MdContacts,
  MdStyle
} from 'react-icons/md';
import Orders from './Orders';
import axiosInstance from '../../../utils/axios';
import Products from './Products';
import Categories from './Categories';
import UploadVideo from './UploadVideo';
import './Dashboard.scss';
import HomeImages from './HomeImages';
import PersonalisationImages from './PersonalisationImages';
import ContactForms from './ContactForms';
import PersonalisationMatieres from './PersonalisationMatieres';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalOrders: 0  
  });
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    currentPassword: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    try {
      // Le cookie HttpOnly est envoyé automatiquement avec withCredentials
      const res = await axiosInstance.get('/admin/me');
    } catch (err) {
      console.error('Auth check failed:', err);
      navigate('/admin/login');
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const productsResponse = await axiosInstance.get('/products?limit=1');
      const totalProducts = productsResponse.data.pagination?.total_products || 0;
      const categoriesResponse = await axiosInstance.get('/categories');
      const totalCategories = categoriesResponse.data.length;
      const ordersResponse = await axiosInstance.get('/orders');
      const totalOrders = ordersResponse.data.length;
      
      setStats({
        totalProducts,
        totalCategories,
        totalOrders,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminEmail');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/admin/logout');
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/admin/login');
  };


  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    
    // Validate passwords match if changing password
    if (profileForm.password && profileForm.password !== profileForm.confirmPassword) {
      setProfileError('Passwords do not match');
      return;
    }
    
    try {
      setProfileLoading(true);
      
      const updateData = {
        currentPassword: profileForm.currentPassword,
        ...(profileForm.email && { email: profileForm.email }),
        ...(profileForm.password && { password: profileForm.password })
      };
      
      const response = await axiosInstance.put('/admin', updateData);
      
      // Update stored email if it was changed
      if (profileForm.email) {
        localStorage.setItem('adminEmail', profileForm.email);
      }
      
      setProfileSuccess('Profile updated successfully');
      setProfileForm({
        currentPassword: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowProfileModal(false);
      }, 2000);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 401) {
        setProfileError('Current password is incorrect');
      } else if (error.response?.status === 409) {
        setProfileError('Email is already in use');
      } else {
        setProfileError(error.response?.data?.error || 'Error updating profile');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openProfileModal = () => {
    setProfileForm({
      currentPassword: '',
      email: localStorage.getItem('adminEmail') || '',
      password: '',
      confirmPassword: ''
    });
    setProfileError('');
    setProfileSuccess('');
    setShowProfileModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TND'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Mobile Menu Button */}
      <button className="mobile-menu-btn" onClick={toggleSidebar}>
        {sidebarOpen ? <MdClose /> : <MdMenu />}
      </button>

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Michkette</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => handleTabChange('products')}
          >
            <MdInventory />
            <span>Products</span>
          </button>
          
          <button
            className={`nav-item ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => handleTabChange('categories')}
          >
            <MdCategory />
            <span>Categories</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => handleTabChange('orders')}
          >
            <MdShoppingCart />
            <span>Orders</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => handleTabChange('contacts')}
          >
            <MdContacts />
            <span>Contact Forms</span>
          </button>
          
          <button
            className={`nav-item ${activeTab === 'matieres' ? 'active' : ''}`}
            onClick={() => handleTabChange('matieres')}
          >
            <MdStyle />
            <span>Personalization Matieres</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'video' ? 'active' : ''}`}
            onClick={() => handleTabChange('video')}
          >
            <MdVideoLibrary />
            <span>About Us Video</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'home-images' ? 'active' : ''}`}
            onClick={() => handleTabChange('home-images')}
          >
            <MdImage />
            <span>Home Images</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'perso-images' ? 'active' : ''}`}
            onClick={() => handleTabChange('perso-images')}
          >
            <MdImage />
            <span>Personalisation Images</span>
          </button>
          <div className="sidebar-footer">
            <button className="nav-item" onClick={openProfileModal}>
              <MdSettings />
              <span>Profile Settings</span>
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              <MdLogout />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <h1>Dashboard</h1>
            <div className="admin-info">
              <MdPerson />
              <span>{localStorage.getItem('adminEmail')}</span>
            </div>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <MdInventory />
            </div>
            <div className="stat-content">
              <h3>Total Products</h3>
              <p>{stats.totalProducts}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <MdCategory />
            </div>
            <div className="stat-content">
              <h3>Total Categories</h3>
              <p>{stats.totalCategories}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <MdShoppingCart />
            </div>
            <div className="stat-content">
              <h3>Total Orders</h3>
              <p>{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="dashboard-content">
          {activeTab === 'products' && <Products />}
          {activeTab === 'categories' && <Categories />}
          {activeTab === 'orders' && <Orders />}
          {activeTab === 'contacts' && <ContactForms />}
          {activeTab === 'matieres' && <PersonalisationMatieres />}
          {activeTab === 'video' && <UploadVideo />}
          {activeTab === 'home-images' && <HomeImages />}
          {activeTab === 'perso-images' && <PersonalisationImages />}
        </div>
      </main>

      {/* Profile Settings Modal */}
      {showProfileModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Profile Settings</h3>
              <button onClick={() => setShowProfileModal(false)}>
                <MdClose />
              </button>
            </div>
            
            <form onSubmit={handleProfileUpdate}>
              {profileError && (
                <div className="error-message">
                  {profileError}
                </div>
              )}
              
              {profileSuccess && (
                <div className="success-message">
                  {profileSuccess}
                </div>
              )}
              
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={profileForm.currentPassword}
                  onChange={handleProfileInputChange}
                  placeholder="Enter current password"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>New Email</label>
                <input
                  type="email"
                  name="email"
                  value={profileForm.email}
                  onChange={handleProfileInputChange}
                  placeholder="Enter new email"
                />
              </div>
              
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="password"
                  value={profileForm.password}
                  onChange={handleProfileInputChange}
                  placeholder="Enter new password"
                />
              </div>
              
              {profileForm.password && (
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={profileForm.confirmPassword}
                    onChange={handleProfileInputChange}
                    placeholder="Confirm new password"
                  />
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowProfileModal(false)}
                  disabled={profileLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary"
                  disabled={profileLoading}
                >
                  {profileLoading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}
    </div>
  );
};

export default Dashboard;